import {
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  inject,
  ViewChild,
} from '@angular/core';
import { MatStepper } from '@angular/material/stepper';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ICreateTema,
  IEfectoFullscreen,
  ITema,
  IUpdateTema,
} from 'modelos/src';
import { Subscription } from 'rxjs';
import { TemasService } from '../../../../../auxiliares/servicios/http/temas.service';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { TemaRuntimeService } from '../../../../../auxiliares/servicios/tema-runtime.service';
import { DateRangeValue } from '../../../../../componentes/date-picker/date-picker.component';

type ModoActivacion = 'permanente' | 'rangoAbsoluto' | 'recurrenteAnual';

type SlotDecal = 'Avatar' | 'LogoLogin' | 'Topbar' | 'Login';

/** Posición de decorado con su nombre legible y dónde aparece */
interface IDecoradoSlot {
  slot: SlotDecal;
  nombre: string;
  ayuda: string;
  /** Qué mide el tamaño %; sin valor, el tamaño no aplica a esa posición */
  tamano?: string;
}

// Las rutas son 'temas/crear' y 'temas/editar/:id' (una sola ruta cada una):
// '..' relativo subía hasta Administración en vez de volver al listado.
const LISTADO_TEMAS = '/main/administracion/temas';

@Component({
  selector: 'app-crear-editar-tema',
  templateUrl: './crear-editar-tema.component.html',
  styleUrl: './crear-editar-tema.component.scss',
  standalone: false,
  encapsulation: ViewEncapsulation.None,
})
export class CrearEditarTemaComponent implements OnInit, OnDestroy {
  @ViewChild('stepper') stepper?: MatStepper;
  private fb = inject(FormBuilder);
  private service = inject(TemasService);
  private helper = inject(HelperService);
  private runtime = inject(TemaRuntimeService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  public form!: FormGroup;
  public id?: string;
  public tema?: ITema;
  public loading = false;
  public guardando = false;
  public subiendo = false;

  public readonly decorados: IDecoradoSlot[] = [
    {
      slot: 'Avatar',
      nombre: 'Avatar del usuario (menú lateral)',
      ayuda:
        'Se dibuja sobre la foto o las iniciales del usuario, arriba del menú lateral. Ocupa todo el avatar.',
    },
    {
      slot: 'Topbar',
      nombre: 'Barra superior',
      ayuda: 'A la derecha del logo de IRIX, en la barra superior.',
      tamano: 'Porcentaje del alto de la barra (como máximo 40 px).',
    },
    {
      slot: 'LogoLogin',
      nombre: 'Logo del login',
      ayuda:
        'En la esquina superior derecha del logo de IRIX, en la pantalla de ingreso.',
      tamano: 'Porcentaje del ancho del logo.',
    },
    {
      slot: 'Login',
      nombre: 'Tarjeta del login',
      ayuda:
        'En la esquina inferior derecha de la tarjeta de ingreso (usuario y contraseña).',
      tamano: 'Porcentaje del ancho de la tarjeta.',
    },
  ];

  /** Tema armado con el formulario: alimenta la vista previa lateral */
  public preview?: ITema;
  /** Efecto reducido para la miniatura (menos partículas y más chicas) */
  public efectoMini?: IEfectoFullscreen;
  private claveEfectoMini?: string;

  public get logoIrix(): string {
    return this.helper.darkTheme
      ? 'assets/images/irix_logo_dark.png'
      : 'assets/images/irix_logo.png';
  }

  private formSub?: Subscription;

  async ngOnInit() {
    this.id = this.route.snapshot.paramMap.get('id') ?? undefined;
    this.buildForm();
    if (this.id) {
      await this.cargar();
    }
    this.formSub = this.form.valueChanges.subscribe(() => this.emitirPreview());
    this.emitirPreview();
  }

  ngOnDestroy(): void {
    this.formSub?.unsubscribe();
    this.runtime.clearPreview();
  }

  private buildForm() {
    this.form = this.fb.group({
      nombre: ['', Validators.required],
      descripcion: [''],
      activa: [false],
      prioridad: [0, [Validators.min(0), Validators.max(100)]],
      modoActivacion: ['permanente' as ModoActivacion],
      rangoFechas: [null as DateRangeValue | null],
      recIniDate: [null as string | null],
      recFinDate: [null as string | null],
      decalAvatarTamPct: [35],
      decalLogoLoginTamPct: [30],
      decalTopbarTamPct: [100],
      decalLoginTamPct: [25],
      efectoActivo: [false],
      efectoCantidad: [32, [Validators.min(1), Validators.max(128)]],
      efectoVelMin: [10],
      efectoVelMax: [20],
      efectoTamMin: [12],
      efectoTamMax: [20],
      efectoDrift: [30],
    });
  }

  private async cargar() {
    if (!this.id) return;
    this.loading = true;
    try {
      this.tema = await this.service.getById(this.id);
      const t = this.tema;
      const modo: ModoActivacion = t.diasRecurrentes
        ? 'recurrenteAnual'
        : t.fechaInicio && t.fechaFin
          ? 'rangoAbsoluto'
          : 'permanente';
      const rangoFechas: DateRangeValue | null =
        t.fechaInicio && t.fechaFin
          ? { start: t.fechaInicio, end: t.fechaFin }
          : null;
      const recIniDate = t.diasRecurrentes
        ? this.diaMesAIso(
            t.diasRecurrentes.inicio.mes,
            t.diasRecurrentes.inicio.dia,
          )
        : null;
      const recFinDate = t.diasRecurrentes
        ? this.diaMesAIso(t.diasRecurrentes.fin.mes, t.diasRecurrentes.fin.dia)
        : null;
      this.form.patchValue({
        nombre: t.nombre ?? '',
        descripcion: t.descripcion ?? '',
        activa: !!t.activa,
        prioridad: t.prioridad ?? 0,
        modoActivacion: modo,
        rangoFechas,
        recIniDate,
        recFinDate,
        decalAvatarTamPct: t.payload?.decalAvatar?.tamanoPct ?? 35,
        decalLogoLoginTamPct: t.payload?.decalLogoLogin?.tamanoPct ?? 30,
        decalTopbarTamPct: t.payload?.decalTopbar?.tamanoPct ?? 100,
        decalLoginTamPct: t.payload?.decalLogin?.tamanoPct ?? 25,
        efectoActivo: !!t.payload?.efectoFullscreen,
        efectoCantidad: t.payload?.efectoFullscreen?.cantidad ?? 32,
        efectoVelMin: t.payload?.efectoFullscreen?.velocidad?.min ?? 10,
        efectoVelMax: t.payload?.efectoFullscreen?.velocidad?.max ?? 20,
        efectoTamMin: t.payload?.efectoFullscreen?.tamano?.min ?? 12,
        efectoTamMax: t.payload?.efectoFullscreen?.tamano?.max ?? 20,
        efectoDrift: t.payload?.efectoFullscreen?.drift ?? 30,
      });
    } catch (e) {
      console.error(e);
      this.helper.notifError(e);
    } finally {
      this.loading = false;
    }
  }

  private diaMesAIso(mes: number, dia: number): string {
    const año = new Date().getFullYear();
    return new Date(año, mes, dia).toISOString();
  }

  /** Construye un ITema de preview combinando el form con los assets ya guardados. */
  private getTemaPreview(): ITema {
    const v = this.form.value;
    const base: ITema = {
      _id: this.id,
      nombre: v.nombre,
      descripcion: v.descripcion,
      activa: true,
      prioridad: +v.prioridad || 0,
      global: true,
      payload: { ...(this.tema?.payload ?? {}) },
    };

    if (v.modoActivacion === 'rangoAbsoluto' && v.rangoFechas) {
      base.fechaInicio = v.rangoFechas.start;
      base.fechaFin = v.rangoFechas.end;
    }
    if (
      v.modoActivacion === 'recurrenteAnual' &&
      v.recIniDate &&
      v.recFinDate
    ) {
      const ini = new Date(v.recIniDate);
      const fin = new Date(v.recFinDate);
      base.diasRecurrentes = {
        inicio: { mes: ini.getMonth(), dia: ini.getDate() },
        fin: { mes: fin.getMonth(), dia: fin.getDate() },
      };
    }

    const p: any = base.payload;
    if (p.decalAvatar) p.decalAvatar.tamanoPct = +v.decalAvatarTamPct;
    if (p.decalLogoLogin) p.decalLogoLogin.tamanoPct = +v.decalLogoLoginTamPct;
    if (p.decalTopbar) p.decalTopbar.tamanoPct = +v.decalTopbarTamPct;
    if (p.decalLogin) p.decalLogin.tamanoPct = +v.decalLoginTamPct;

    if (v.efectoActivo && p.efectoFullscreen) {
      p.efectoFullscreen.cantidad = +v.efectoCantidad;
      p.efectoFullscreen.velocidad = {
        min: +v.efectoVelMin,
        max: +v.efectoVelMax,
      };
      p.efectoFullscreen.tamano = {
        min: +v.efectoTamMin,
        max: +v.efectoTamMax,
      };
      p.efectoFullscreen.drift = +v.efectoDrift;
    } else if (!v.efectoActivo) {
      p.efectoFullscreen = undefined;
    }

    return base;
  }

  private emitirPreview() {
    if (!this.form) return;
    this.preview = this.getTemaPreview();
    this.actualizarEfectoMini(this.preview.payload?.efectoFullscreen);
    this.runtime.setPreview(this.preview);
  }

  /**
   * Sólo se regenera si cambió el efecto: así las partículas de la
   * miniatura no se reinician con cada tecla de otro campo.
   */
  private actualizarEfectoMini(efecto?: IEfectoFullscreen | null) {
    const clave = efecto ? JSON.stringify(efecto) : '';
    if (clave === this.claveEfectoMini) return;
    this.claveEfectoMini = clave;
    if (!efecto?.spriteUrl) {
      this.efectoMini = undefined;
      return;
    }
    const escala = 0.5;
    this.efectoMini = {
      ...efecto,
      cantidad: Math.min(efecto.cantidad ?? 32, 14),
      tamano: {
        min: (efecto.tamano?.min ?? 12) * escala,
        max: (efecto.tamano?.max ?? 20) * escala,
      },
      drift: (efecto.drift ?? 30) * escala,
    };
  }

  public urlDecal(slot: SlotDecal): string | undefined {
    const p = this.tema?.payload;
    if (!p) return undefined;
    switch (slot) {
      case 'Avatar':
        return p.decalAvatar?.urlAsset;
      case 'LogoLogin':
        return p.decalLogoLogin?.urlAsset;
      case 'Topbar':
        return p.decalTopbar?.urlAsset;
      case 'Login':
        return p.decalLogin?.urlAsset;
      default:
        return undefined;
    }
  }

  public urlSprite(): string | undefined {
    return this.tema?.payload?.efectoFullscreen?.spriteUrl;
  }

  public async onUploadDecal(slot: SlotDecal, ev: Event) {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file || !this.id) return;
    await this.subirAsset(file, async (url) => {
      const tamPct = this.form.value[`decal${slot}TamPct`] as number;
      const decal = { urlAsset: url, tamanoPct: tamPct };
      const payload: any = { ...(this.tema?.payload ?? {}) };
      payload[`decal${slot}`] = decal;
      await this.service.update(this.id!, { payload });
      await this.cargar();
      this.emitirPreview();
    });
    input.value = '';
  }

  public async onUploadSprite(ev: Event) {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file || !this.id) return;
    await this.subirAsset(file, async (url) => {
      const v = this.form.value;
      const payload: any = { ...(this.tema?.payload ?? {}) };
      payload.efectoFullscreen = {
        ...(payload.efectoFullscreen ?? {}),
        motor: 'particulas',
        spriteUrl: url,
        cantidad: v.efectoCantidad ?? 32,
        velocidad: { min: v.efectoVelMin ?? 10, max: v.efectoVelMax ?? 20 },
        tamano: { min: v.efectoTamMin ?? 12, max: v.efectoTamMax ?? 20 },
        drift: v.efectoDrift ?? 30,
      };
      await this.service.update(this.id!, { payload });
      await this.cargar();
      this.emitirPreview();
    });
    input.value = '';
  }

  private async subirAsset(file: File, after: (url: string) => Promise<void>) {
    if (!this.id) {
      this.helper.notifWarn('Guardá el tema antes de subir assets');
      return;
    }
    if (!['image/png', 'image/webp'].includes(file.type)) {
      this.helper.notifWarn('Solo PNG o WebP');
      return;
    }
    if (file.size > 1_048_576) {
      this.helper.notifWarn('Máximo 1MB');
      return;
    }
    this.subiendo = true;
    try {
      const { url } = await this.service.uploadAsset(this.id, file);
      await after(url);
      this.helper.notifSuccess('Asset subido');
    } catch (e) {
      console.error(e);
      this.helper.notifError(e);
    } finally {
      this.subiendo = false;
    }
  }

  public async guardar() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.guardando = true;
    try {
      const v = this.form.value;
      const base: any = {
        nombre: v.nombre,
        descripcion: v.descripcion || undefined,
        activa: !!v.activa,
        prioridad: +v.prioridad || 0,
      };

      if (v.modoActivacion === 'rangoAbsoluto') {
        base.fechaInicio = v.rangoFechas?.start ?? null;
        base.fechaFin = v.rangoFechas?.end ?? null;
        base.diasRecurrentes = null;
      } else if (v.modoActivacion === 'recurrenteAnual') {
        const ini = v.recIniDate ? new Date(v.recIniDate) : null;
        const fin = v.recFinDate ? new Date(v.recFinDate) : null;
        if (!ini || !fin) {
          this.helper.notifWarn('Falta seleccionar fechas recurrentes');
          this.guardando = false;
          return;
        }
        base.diasRecurrentes = {
          inicio: { mes: ini.getMonth(), dia: ini.getDate() },
          fin: { mes: fin.getMonth(), dia: fin.getDate() },
        };
        base.fechaInicio = null;
        base.fechaFin = null;
      } else {
        base.fechaInicio = null;
        base.fechaFin = null;
        base.diasRecurrentes = null;
      }

      const payload: any = { ...(this.tema?.payload ?? {}) };
      if (payload.decalAvatar)
        payload.decalAvatar.tamanoPct = +v.decalAvatarTamPct;
      if (payload.decalLogoLogin)
        payload.decalLogoLogin.tamanoPct = +v.decalLogoLoginTamPct;
      if (payload.decalTopbar)
        payload.decalTopbar.tamanoPct = +v.decalTopbarTamPct;
      if (payload.decalLogin)
        payload.decalLogin.tamanoPct = +v.decalLoginTamPct;
      if (v.efectoActivo && payload.efectoFullscreen) {
        payload.efectoFullscreen.cantidad = +v.efectoCantidad;
        payload.efectoFullscreen.velocidad = {
          min: +v.efectoVelMin,
          max: +v.efectoVelMax,
        };
        payload.efectoFullscreen.tamano = {
          min: +v.efectoTamMin,
          max: +v.efectoTamMax,
        };
        payload.efectoFullscreen.drift = +v.efectoDrift;
      } else if (!v.efectoActivo) {
        payload.efectoFullscreen = undefined;
      }
      base.payload = payload;

      if (this.id) {
        await this.service.update(this.id, base as IUpdateTema);
        this.helper.notifSuccess('Tema actualizado');
        await this.runtime.refresh();
      } else {
        const created = await this.service.create(base as ICreateTema);
        this.id = created._id;
        this.tema = created;
        this.helper.notifSuccess('Tema creado. Ya podés subir assets.');
        this.router.navigate([LISTADO_TEMAS, 'editar', this.id]);
        return;
      }
    } catch (e) {
      console.error(e);
      this.helper.notifError(e);
    } finally {
      this.guardando = false;
    }
  }

  public volver() {
    this.router.navigate([LISTADO_TEMAS]);
  }
}
